/**
 * Diamond Mega Menu — Vanilla JS
 * Handles: hover/keyboard open-close, ARIA state, Esc/Tab/Arrow nav,
 * live-region SR announcements, mobile drawer.
 */

class DiamondMegaMenu {
  constructor() {
    this.nav = document.getElementById('fj-nav');
    if (!this.nav) return;

    this.items     = this.nav.querySelectorAll('.fj-nav__item--has-dropdown');
    this.triggers  = this.nav.querySelectorAll('[data-menu-trigger]');
    this.announcer = document.getElementById('fj-menu-announcer');
    this.current   = null;   // currently open trigger element
    this.closeTimer = null;

    this._positionPanels();
    window.addEventListener('resize', () => this._positionPanels());
    window.addEventListener('scroll', () => this._positionPanels(), { passive: true });

    this._bindEvents();
  }

  /* ── Dynamic panel positioning ─────────────────────────────── */
  // Measures the actual bottom of the sticky header at runtime so the
  // megamenu top is correct regardless of announcement bars or theme
  // CSS variables being overridden by the parent theme.
  _positionPanels() {
    const header = document.getElementById('fj-header');
    if (!header) return;
    // Use the nav's bottom edge (visible bottom of the header bar)
    const navBottom = this.nav.getBoundingClientRect().bottom;
    const top = Math.round(navBottom) + 'px';
    document.querySelectorAll('.fj-megamenu').forEach(panel => {
      panel.style.top = top;
    });
  }

  /* ── Event wiring ─────────────────────────────────────────── */

  _bindEvents() {
    this.items.forEach(item => {
      const trigger = item.querySelector('[data-menu-trigger]');
      const panel   = item.querySelector('[data-menu-panel]');
      if (!trigger || !panel) return;

      // Hover (pointer devices only — avoids double-fire on touch)
      item.addEventListener('mouseenter', () => {
        clearTimeout(this.closeTimer);
        this._open(trigger, panel);
      });
      item.addEventListener('mouseleave', () => {
        this.closeTimer = setTimeout(() => this._close(trigger, panel, false), 120);
      });

      // Click / keyboard toggle
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this._isOpen(trigger)) {
          this._close(trigger, panel, true);
        } else {
          this._closeAll(false);
          this._open(trigger, panel);
        }
      });

      // Arrow down from trigger → focus first panel link
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (!this._isOpen(trigger)) this._open(trigger, panel);
          const first = panel.querySelector('a, button');
          if (first) first.focus();
        }
        if (e.key === 'Escape') {
          this._close(trigger, panel, true);
        }
      });

      // Escape from anywhere inside the panel → return focus to trigger
      panel.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          this._close(trigger, panel, true);
        }
      });

      // Keep open while panel itself has focus
      panel.addEventListener('focusin', () => clearTimeout(this.closeTimer));
      panel.addEventListener('focusout', (e) => {
        // Only close if focus leaves both trigger and panel
        if (!item.contains(e.relatedTarget)) {
          this.closeTimer = setTimeout(() => this._close(trigger, panel, false), 120);
        }
      });
    });

    // Global Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this._closeAll(true);
    });

    // Click outside closes
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.fj-nav__item--has-dropdown')) {
        this._closeAll(false);
      }
    });
  }

  /* ── Open / Close helpers ─────────────────────────────────── */

  _open(trigger, panel) {
    // Close any other open menu first
    if (this.current && this.current !== trigger) {
      const prevPanel = document.querySelector(`[data-menu-panel="${this.current.dataset.menuTrigger}"]`);
      if (prevPanel) this._close(this.current, prevPanel, false);
    }

    trigger.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');
    panel.classList.add('is-open');
    this.current = trigger;

    const label = trigger.querySelector('.fj-nav__trigger-text')?.textContent?.trim();
    if (label) this._announce(`${label} menu expanded`);
  }

  _close(trigger, panel, returnFocus) {
    trigger.setAttribute('aria-expanded', 'false');
    panel.setAttribute('aria-hidden', 'true');
    panel.classList.remove('is-open');
    if (this.current === trigger) this.current = null;

    if (returnFocus) {
      trigger.focus();
      const label = trigger.querySelector('.fj-nav__trigger-text')?.textContent?.trim();
      if (label) this._announce(`${label} menu collapsed`);
    }
  }

  _closeAll(returnFocus) {
    if (!this.current) return;
    const panel = document.querySelector(`[data-menu-panel="${this.current.dataset.menuTrigger}"]`);
    if (panel) this._close(this.current, panel, returnFocus);
  }

  _isOpen(trigger) {
    return trigger.getAttribute('aria-expanded') === 'true';
  }

  _announce(msg) {
    if (!this.announcer) return;
    this.announcer.textContent = '';
    // Tiny defer so SR re-reads even identical messages
    requestAnimationFrame(() => {
      this.announcer.textContent = msg;
    });
  }
}


/* ── Mobile Drawer ────────────────────────────────────────────── */

class MobileMenu {
  constructor() {
    this.toggle  = document.getElementById('fj-mobile-toggle');
    this.menu    = document.getElementById('fj-mobile-menu');
    this.overlay = this.menu?.querySelector('.fj-mobile-menu__overlay');
    this.closeBtn= this.menu?.querySelector('.fj-mobile-menu__close');
    if (!this.toggle || !this.menu) return;
    this._bindEvents();
  }

  _bindEvents() {
    this.toggle.addEventListener('click', () => this._open());
    this.closeBtn?.addEventListener('click', () => this._close());
    this.overlay?.addEventListener('click', () => this._close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._isOpen()) this._close();
    });
    // Trap focus within drawer while open
    this.menu.addEventListener('keydown', (e) => this._trapFocus(e));
  }

  _open() {
    this.menu.setAttribute('aria-hidden', 'false');
    this.menu.classList.add('is-open');
    this.toggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add('fj-body--menu-open');
    // Focus the close button
    setTimeout(() => this.closeBtn?.focus(), 50);
  }

  _close() {
    this.menu.setAttribute('aria-hidden', 'true');
    this.menu.classList.remove('is-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('fj-body--menu-open');
    this.toggle.focus();
  }

  _isOpen() {
    return this.menu.classList.contains('is-open');
  }

  _trapFocus(e) {
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      this.menu.querySelectorAll('a, button, input, [tabindex]:not([tabindex="-1"])')
    ).filter(el => !el.closest('[aria-hidden="true"]') && !el.hidden);

    if (!focusable.length) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}


/* ── Sticky header shrink ─────────────────────────────────────── */

class StickyHeader {
  constructor() {
    this.header = document.getElementById('fj-header');
    if (!this.header) return;
    this._lastY = 0;
    window.addEventListener('scroll', () => this._onScroll(), { passive: true });
  }

  _onScroll() {
    const y = window.scrollY;
    if (y > 80) {
      this.header.classList.add('fj-header--scrolled');
    } else {
      this.header.classList.remove('fj-header--scrolled');
    }
    this._lastY = y;
  }
}


/* ── Boot ────────────────────────────────────────────────────── */

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

function boot() {
  new DiamondMegaMenu();
  new MobileMenu();
  new StickyHeader();
}
